<template>
    <v-dialog
        persistent
        v-model="dialog"
        max-width="1500px"
        :retain-focus="false"
    >
        <v-card :loading="loading" height="85vh" class="d-flex flex-column">
            <v-card-title class="text-h5">
                {{ selectedQuarter }}
            </v-card-title>
            <v-card-text
                class="flex-grow-1 overflow-hidden d-flex flex-column pa-4"
            >
                <p class="mb-2 flex-shrink-0">
                    {{ getFormattedRangeFromQuarter(selectedQuarter) }}
                </p>

                <div style="flex: 1 1 0; overflow-y: auto; min-height: 0;">
                    <v-data-table
                        :headers="headers"
                        :items="sortedSelectedProjections || []"
                        disable-pagination
                        hide-default-footer
                        :mobile-breakpoint="0"
                        class="mt-2"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                    >
                        <template v-slot:top>
                            <div>
                                <v-row
                                    no-gutters
                                    style="background-color: #eeeeee"
                                    class="d-flex justify-start py-4 mx-0 flex-wrap"
                                >
                                    <div
                                        v-for="(row, index) in currencySummary"
                                        :key="'cc-' + row.currency"
                                        class="d-flex"
                                    >
                                        <v-card
                                            class="pt-5 px-2 ma-2"
                                            min-width="250px"
                                        >
                                            <v-card-text
                                                class="d-flex my-0 py-0 mx-0 px-0"
                                                width="100%"
                                            >
                                                <v-chip
                                                    class="mb-1 mt-0 mx-2 pt-0 white--text"
                                                    :color="
                                                        currencyCardStyle(index)
                                                            .color
                                                    "
                                                    label
                                                    style="height: 50px; flex-shrink: 0"
                                                >
                                                    <v-icon center>{{
                                                        currencyCardStyle(index)
                                                            .icon
                                                    }}</v-icon>
                                                </v-chip>
                                                <div class="mb-1 mt-0 pt-0">
                                                    <div
                                                        class="d-flex mr-0 pr-0 text-capitalize"
                                                    >
                                                        <span
                                                            style="font-size: 20px; font-weight: bold;"
                                                            >{{
                                                                row.currency
                                                            }}</span
                                                        >
                                                    </div>
                                                    <p
                                                        class="text-h6 black--text"
                                                    >
                                                        ({{ row.count }})
                                                        {{
                                                            formatCurrency(
                                                                row.currency,
                                                                row.sum
                                                            )
                                                        }}
                                                    </p>
                                                </div>
                                            </v-card-text>
                                        </v-card>
                                    </div>
                                </v-row>
                            </div>
                            <v-toolbar flat color="grey lighten-3">
                                <v-toolbar-title>QUOTES</v-toolbar-title>
                                <!-- Probability filter chips -->
                                <div
                                    class="ml-5"
                                    style="
                                        position: relative;
                                        border: 1px solid rgba(0, 0, 0, 0.38);
                                        border-radius: 4px;
                                        padding: 8px 12px;
                                        display: inline-flex;
                                        align-items: center;
                                    "
                                >
                                    <v-chip
                                        small
                                        :color="
                                            filterProbability === 'all'
                                                ? 'primary'
                                                : 'default'
                                        "
                                        class="mr-2"
                                        @click="filterProbability = 'all'"
                                    >
                                        All
                                    </v-chip>
                                    <v-chip
                                        small
                                        :color="
                                            filterProbability === 'high'
                                                ? '#23bf85'
                                                : 'default'
                                        "
                                        :text-color="
                                            filterProbability === 'high'
                                                ? 'white'
                                                : ''
                                        "
                                        class="mr-2"
                                        @click="filterProbability = 'high'"
                                    >
                                        High
                                    </v-chip>
                                    <v-chip
                                        small
                                        :color="
                                            filterProbability === 'low'
                                                ? '#E74C3C'
                                                : 'default'
                                        "
                                        :text-color="
                                            filterProbability === 'low'
                                                ? 'white'
                                                : ''
                                        "
                                        @click="filterProbability = 'low'"
                                    >
                                        Low
                                    </v-chip>
                                </div>
                                <v-spacer></v-spacer>
                            </v-toolbar>
                        </template>
                        <template v-slot:[`header.finalTotal`]="{ header }">
                            <span>{{ header.text }}</span>
                            <v-btn
                                text
                                icon
                                small
                                class="ml-1"
                                @click="activateSort('finalTotal')"
                            >
                                <v-icon
                                    v-if="sortBy === 'finalTotal' && !sortDesc"
                                    >mdi-arrow-up-bold</v-icon
                                >
                                <v-icon
                                    v-else-if="
                                        sortBy === 'finalTotal' && sortDesc
                                    "
                                    >mdi-arrow-down-bold</v-icon
                                >
                                <!-- neutral icon -->
                                <v-icon v-else>mdi-swap-vertical</v-icon>
                            </v-btn>
                        </template>
                        <template v-slot:[`item.clientId`]="{ item }">
                            <div>
                                {{ getClientName(item.clientId) }}
                            </div>
                        </template>
                        <template v-slot:[`item.probability`]="{ item }">
                            <div>
                                <v-btn
                                    style="width: 90px;"
                                    rounded
                                    small
                                    elevation="0"
                                    :color="`${defineColor(item.probability)}`"
                                    class="text-center btnStatus white--text ml-2"
                                >
                                    {{ item.probability.toUpperCase() }}
                                </v-btn>
                            </div>
                        </template>

                        <template v-slot:[`item.userId`]="{ item }">
                            <div>
                                <p class="my-0">
                                    {{ getUserName(item.userId) }}
                                </p>
                            </div>
                        </template>

                        <template v-slot:[`item.finalTotal`]="{ item }">
                            <div class="text-end">
                                {{
                                    formatCurrency(
                                        item.currency,
                                        +item.finalTotal
                                    )
                                }}
                            </div>
                        </template>
                    </v-data-table>
                </div>
            </v-card-text>
            <v-card-actions class="flex-shrink-0">
                <v-btn
                    text
                    color="secondary"
                    @click="closeDialog"
                    class="mt-n3 mb-3"
                >
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'
import moment from 'moment'

const CURRENCY_CARD_STYLES = [
    { color: '#1976d2', icon: 'mdi-cash-multiple' },
    { color: '#23bf85', icon: 'mdi-cash-multiple' },
    { color: '#E74C3C', icon: 'mdi-cash-multiple' },
    { color: '#F39C12', icon: 'mdi-cash-multiple' },
    { color: '#492970', icon: 'mdi-cash-multiple' },
]

export default {
    name: 'QuotesTable',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        selectedQuarter: {
            type: String,
            default: '',
        },
        projections: {
            type: Array,
            default: () => [],
        },
        companyId: {
            type: String,
            default: null,
        },
        formattedRange: {
            type: String,
            default: '',
        },
        users: {
            type: Array,
            default: () => [],
        },
        clients: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            headers: [
                {
                    text: 'ID',
                    value: 'number',
                    sortable: true,
                },
                {
                    text: 'Name',
                    value: 'name',
                    align: 'center',
                    sortable: true,
                },
                {
                    text: 'Client',
                    value: 'clientId',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'City',
                    value: 'city',
                    align: 'center',
                    sortable: true,
                },
                {
                    text: 'Account Manager',
                    value: 'userId',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Expectation',
                    value: 'probability',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Price',
                    value: 'finalTotal',
                    align: 'end',
                    sortable: false,
                },
            ],
            quotes: [],
            loading: false,
            sortBy: 'finalTotal',
            sortDesc: true,
            filterProbability: 'high',
        }
    },
    computed: {
        dialog: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value)
            },
        },
        probabilityFilteredQuotes() {
            let filtered = this.quotes
            if (this.filterProbability !== 'all') {
                filtered = this.quotes.filter(
                    q =>
                        q.probability &&
                        q.probability.toLowerCase() === this.filterProbability
                )
            }
            return filtered
        },
        currencySummary() {
            const byCode = {}
            for (const q of this.probabilityFilteredQuotes) {
                const raw = q.currency != null ? String(q.currency).trim() : ''
                const code = raw ? raw.toUpperCase() : 'N/D'
                if (!byCode[code]) {
                    byCode[code] = { currency: code, count: 0, sum: 0 }
                }
                byCode[code].count += 1
                byCode[code].sum += Number(q.total) || 0
            }
            return Object.values(byCode).sort((a, b) =>
                a.currency.localeCompare(b.currency)
            )
        },
        sortedSelectedProjections() {
            return this.probabilityFilteredQuotes.map(quote => ({
                ...quote,
                finalTotal: Number(quote.total) || 0,
            }))
        },
    },
    async mounted() {
        try {
            this.loading = true
            const quoteIds = [...new Set(this.projections.map(p => p.id))]
            const quotes = await API.getSimpleQuotesByIds({
                quoteIds,
                companyId: this.companyId,
            })

            this.projections.forEach(projection => {
                const quote = quotes.find(q => q.id == projection.id)
                if (quote) {
                    this.quotes.push({
                        ...quote,
                        ...projection,
                        number: quote.number,
                    })
                }
            })
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        currencyCardStyle(index) {
            return CURRENCY_CARD_STYLES[index % CURRENCY_CARD_STYLES.length]
        },
        getUserName(userId) {
            const user = this.users.find(user => user.id === userId)
            return user ? user.name : userId
        },
        activateSort(key) {
            if (this.sortBy === key) {
                this.sortDesc = !this.sortDesc
            } else {
                this.sortBy = key
                this.sortDesc = true
            }
        },
        isValidIso4217(currency) {
            if (!currency || typeof currency !== 'string') return false
            return /^[A-Z]{3}$/.test(currency)
        },
        formatCurrency(currency, value) {
            const num = Number(value) || 0
            const code =
                currency != null && String(currency).trim()
                    ? String(currency)
                          .trim()
                          .toUpperCase()
                    : 'N/D'
            if (!this.isValidIso4217(code)) {
                return `${new Intl.NumberFormat('de-DE', {
                    maximumFractionDigits: 0,
                }).format(num)} ${code}`
            }
            try {
                return new Intl.NumberFormat('de-DE', {
                    style: 'currency',
                    currency: code,
                    maximumFractionDigits: 0,
                }).format(num)
            } catch {
                return `${new Intl.NumberFormat('de-DE', {
                    maximumFractionDigits: 0,
                }).format(num)} ${code}`
            }
        },
        closeDialog() {
            this.$emit('input', false)
        },
        getClientName(id) {
            if (this.clients.length > 0) {
                return this.clients.find(client => client.id == id)?.name
            } else {
                return id
            }
        },
        defineColor(status) {
            const probability = status.toLowerCase()
            if (probability == 'high') {
                return '#23bf85'
            } else if (probability == 'medium') {
                return '#F39C12'
            } else if (probability == 'low') {
                return '#E74C3C'
            } else {
                return '#808080'
            }
        },
        getFormattedRangeFromQuarter(quarterString) {
            if (
                !quarterString ||
                typeof quarterString !== 'string' ||
                !quarterString.startsWith('Q')
            ) {
                return 'Date range: N/A'
            }

            const parts = quarterString.split(' ')
            const quarterNumber = parseInt(parts[0].replace('Q', ''))
            const year = parseInt(parts[1])
            if (isNaN(quarterNumber) || isNaN(year)) {
                return 'Date range: Invalid format'
            }
            const startDate = moment()
                .year(year)
                .quarter(quarterNumber)
                .startOf('quarter')
            const endDate = moment()
                .year(year)
                .quarter(quarterNumber)
                .endOf('quarter')
            const formattedStart = startDate.format('MMM DD, YYYY')
            const formattedEnd = endDate.format('MMM DD, YYYY')
            return `Date range: ${formattedStart} to ${formattedEnd}`
        },
    },
}
</script>

<style scoped></style>
